import React, { useState, useEffect, useCallback, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { TERMS_SECTIONS, HOUSE_RULES } from '../data/terms';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import {
  format, parse, addDays, addMonths, startOfMonth, endOfMonth,
  eachDayOfInterval, isSameDay, isBefore, isAfter, startOfDay, endOfDay,
  getDay, startOfWeek, addMonths as addM, subMonths, differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns';
import enAU from 'date-fns/locale/en-AU';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useSpaces } from '../contexts/SpacesContext';
import { useAuth } from '../contexts/AuthContext';
import { createBooking, markTermsAccepted, updateBookingPaymentMethod, markPaymentComplete, updateBookingStatus, getBookingsForSpace, getMockBookings, getMemberCreditBalance, deductMeetingRoomCredits, provisionMonthlyCredits, provisionDailyPassCredits } from '../services/bookingService';
import { createPaymentIntent } from '../services/stripeService';
import { sendWelcomeEmail, sendBookingConfirmation, generateInvoice } from '../services/emailService';
import { getAccessFeatureEnabled, getOrCreateAccessPin } from '../services/accessService';
import useActivePromos, { findPromo, applyPromo } from '../hooks/useActivePromos';
import { calcPrice } from '../utils/pricing';
import { sendBookingSms } from '../services/smsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const normalisePhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.trim().replace(/[\s\-().]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('0')) return '+61' + cleaned.slice(1);
  return cleaned;
};

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// ─── Step indicator ───────────────────────────────────────────────────────────
const steps = [
  { label: 'Choose Space' },
  { label: 'Pick Dates' },
  { label: 'Your Details' },
  { label: 'Payment' },
  { label: 'Confirmed' },
];

const StepBar = ({ current }) => (
  <div className="flex items-center justify-center gap-0 mb-10">
    {steps.map((step, idx) => (
      <React.Fragment key={step.label}>
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
            idx < current ? 'bg-brand-500 text-white' :
            idx === current ? 'bg-brand-500 text-white ring-4 ring-brand-100' :
            'bg-gray-200 text-gray-400'
          }`}>
            {idx < current ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            ) : idx + 1}
          </div>
          <span className={`text-xs mt-1 font-medium hidden sm:block ${idx === current ? 'text-brand-600' : 'text-gray-400'}`}>
            {step.label}
          </span>
        </div>
        {idx < steps.length - 1 && (
          <div className={`w-8 sm:w-12 h-0.5 mx-1 transition-colors ${idx < current ? 'bg-brand-500' : 'bg-gray-200'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ─── Step 0: Choose Space ─────────────────────────────────────────────────────
const SpaceCard = ({ space, selected, onSelect }) => (
  <div
    onClick={() => onSelect(space.id)}
    className={`card cursor-pointer transition-all hover:shadow-md ${selected ? 'ring-2 ring-brand-500' : ''}`}
  >
    <div className="h-1.5 rounded-t-2xl" style={{ backgroundColor: space.color }} />
    <div className="p-5">
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: space.bgColor }}>
            {space.type === 'room' ? (
              <svg className="w-5 h-5" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            ) : (
              <svg className="w-5 h-5" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="font-bold text-slate-800">{space.name}</h3>
            <p className="text-xs text-slate-400">{space.type === 'room' ? `Up to ${space.capacity} people` : `${space.totalSeats} desks`}</p>
          </div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300'}`}>
          {selected && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
        </div>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed mt-1 mb-3">{space.description.substring(0, 100)}…</p>
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-slate-400">From</span>
        <span className="font-bold text-slate-800">
          ${space.pricingOptions[0].price}
          <span className="text-xs font-normal text-slate-400 ml-1">/{space.pricingOptions[0].type === 'hourly' ? 'hr' : space.pricingOptions[0].type === 'daily' ? 'day' : 'mo'}</span>
        </span>
      </div>
    </div>
  </div>
);

// ─── Step 1: Date Range Calendar ──────────────────────────────────────────────
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const isInRange = (day, start, end) => {
  if (!start || !end) return false;
  return isAfter(day, start) && isBefore(day, end);
};

const MONTH_OPTIONS = [1, 6, 12];

const AvailabilityCalendar = ({ space, pricingType, setPricingType, startDate, endDate, onRangeSelect, selectedSeat, onSeatSelect, onContinue, activePromos }) => {
  const [calMonth, setCalMonth] = useState(new Date());
  const [bookedDates, setBookedDates] = useState([]);
  const [selecting, setSelecting] = useState(null); // null | Date (first click)
  const [hoverDate, setHoverDate] = useState(null);
  const [months, setMonths] = useState(1);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(10);
  const [loading, setLoading] = useState(false);
  const [availableSeats, setAvailableSeats] = useState(null);
  const [loadingSeats, setLoadingSeats] = useState(false);

  const isHourly = pricingType === 'hourly';
  const isMonthly = pricingType === 'monthly';
  const today = startOfDay(new Date());

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const start = startOfMonth(calMonth);
      const end = endOfMonth(addM(calMonth, 1));
      let bookings;
      try {
        bookings = await getBookingsForSpace(space.id, start, end);
      } catch {
        bookings = getMockBookings(start, end).filter((b) => b.spaceId === space.id);
      }
      setBookedDates(bookings.map((b) => ({
        start: b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate),
        end: b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate),
      })));
    } finally {
      setLoading(false);
    }
  }, [calMonth, space.id]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const days = eachDayOfInterval({ start: startOfMonth(calMonth), end: endOfMonth(calMonth) });
  const startPad = (getDay(startOfMonth(calMonth)) + 6) % 7;

  const isDayBooked = (day) => {
    if (space.type === 'desk') {
      return bookedDates.filter((b) =>
        !isBefore(day, startOfDay(b.start)) && !isBefore(startOfDay(b.end), day)
      ).length >= space.totalSeats;
    }
    return bookedDates.some((b) =>
      !isBefore(day, startOfDay(b.start)) && !isBefore(startOfDay(b.end), day)
    );
  };

  const handleDayClick = (day) => {
    if (isBefore(day, today) || isDayBooked(day)) return;

    if (isMonthly) {
      // Single click confirms start date; end is auto-computed from months
      onRangeSelect(day, addMonths(day, months));
      return;
    }

    if (!selecting) {
      setSelecting(day);
      onRangeSelect(day, null);
    } else {
      if (isBefore(day, selecting)) {
        setSelecting(day);
        onRangeSelect(day, null);
        return;
      }
      onRangeSelect(selecting, addDays(day, 1));
      setSelecting(null);
    }
  };

  // For monthly, update end when months changes
  useEffect(() => {
    if (isMonthly && startDate) {
      onRangeSelect(startDate, addMonths(startDate, months));
    }
  }, [months, isMonthly]); // eslint-disable-line

  // Load available seats for any desk booking with named seats
  useEffect(() => {
    if (!space.seats?.length || !startDate || !endDate) {
      setAvailableSeats(null);
      return;
    }
    setLoadingSeats(true);
    onSeatSelect(null);
    getBookingsForSpace(space.id, startDate, endDate)
      .then((bookings) => {
        const taken = new Set(bookings.filter((b) => b.seatName).map((b) => b.seatName));
        const generic = bookings.filter((b) => !b.seatName).length;
        const available = space.seats.filter((s) => !taken.has(s));
        setAvailableSeats(available.slice(generic));
      })
      .catch(() => setAvailableSeats(space.seats))
      .finally(() => setLoadingSeats(false));
  }, [startDate?.getTime(), endDate?.getTime(), space.id]); // eslint-disable-line

  // Confirm hourly
  const handleHourlyConfirm = () => {
    if (!startDate) { toast.error('Select a date first.'); return; }
    const s = new Date(startDate); s.setHours(startHour, 0, 0, 0);
    const e = new Date(startDate); e.setHours(endHour, 0, 0, 0);
    if (e <= s) { toast.error('End time must be after start time.'); return; }
    onRangeSelect(s, e);
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 7);

  const durationLabel = () => {
    if (!startDate) return null;
    if (isHourly && endDate) {
      const hrs = Math.round((endDate - startDate) / 60000 / 30) * 30 / 60;
      return `${hrs} hr${hrs !== 1 ? 's' : ''}`;
    }
    if (isMonthly) return `${months} month${months !== 1 ? 's' : ''}`;
    if (endDate) {
      const days = differenceInCalendarDays(endDate, startDate);
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return null;
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Pick Your Dates</h2>
        <p className="text-sm text-slate-500 mt-1">
          Availability for <strong className="text-slate-700">{space.name}</strong>.
          {!isHourly && !isMonthly && ' Click a start date, then an end date.'}
          {isMonthly && ' Click a start date and choose how many months.'}
          {isHourly && ' Click a day and choose your time window.'}
        </p>
      </div>

      {/* Pricing selector */}
      <div className="flex flex-wrap gap-2">
        {space.pricingOptions.map((opt) => {
          const promo = (activePromos || []).find((p) =>
            (p.spaceId === 'all' || p.spaceId === space.id) &&
            (p.pricingType === 'all' || p.pricingType === opt.type)
          );
          const promoPrice = promo
            ? promo.discountType === 'percent'
              ? Math.round(opt.price * (100 - promo.discountValue) / 100)
              : Math.max(0, opt.price - promo.discountValue)
            : null;
          return (
            <button
              key={opt.type}
              onClick={() => { setPricingType(opt.type); setSelecting(null); onRangeSelect(null, null); }}
              className={`text-sm px-4 py-2 rounded-xl border font-medium transition-colors ${
                pricingType === opt.type
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-200 text-slate-500 hover:border-gray-300'
              }`}
            >
              {opt.label} —{' '}
              {promoPrice !== null ? (
                <>
                  <span className="line-through text-slate-400 font-normal">${opt.price}</span>{' '}
                  <strong className="text-brand-600">${promoPrice}</strong>
                </>
              ) : (
                <strong>${opt.price}</strong>
              )}
            </button>
          );
        })}
      </div>

      {/* Promo banner */}
      {(() => {
        const promo = (activePromos || []).find((p) =>
          (p.spaceId === 'all' || p.spaceId === space.id) &&
          (p.pricingType === 'all' || p.pricingType === pricingType)
        );
        if (!promo) return null;
        return (
          <div className="flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-xl px-4 py-2.5 text-sm text-brand-800">
            <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>
              <strong>{promo.name}</strong> — {promo.discountType === 'percent' ? `${promo.discountValue}% off` : `$${promo.discountValue} off`}
              {' '}(until {promo.endDate})
            </span>
          </div>
        );
      })()}

      {/* Calendar */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-gray-200">
          <button
            onClick={() => setCalMonth(subMonths(calMonth, 1))}
            disabled={isBefore(startOfMonth(calMonth), startOfMonth(today))}
            className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-700">{format(calMonth, 'MMMM yyyy')}</span>
            {loading && <span className="w-3.5 h-3.5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />}
          </div>
          <button onClick={() => setCalMonth(addM(calMonth, 1))} className="p-1.5 rounded-lg hover:bg-gray-200">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {DAY_LABELS.map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-slate-400 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 p-2 gap-1">
          {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} />)}
          {days.map((day) => {
            const past = isBefore(day, today);
            const booked = isDayBooked(day);
            const isStart = startDate && isSameDay(day, startDate);
            const isEnd = endDate && isSameDay(day, addDays(endDate, -1));
            const inRange = isInRange(day, startDate, endDate) ||
              (selecting && hoverDate && isInRange(day, selecting, hoverDate));
            const isToday = isSameDay(day, today);
            const disabled = past || booked;

            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => selecting && setHoverDate(day)}
                onMouseLeave={() => setHoverDate(null)}
                disabled={disabled}
                className={`
                  w-full rounded-xl text-sm font-medium flex flex-col items-center justify-center transition-colors relative
                  ${pricingType === 'daily' && !isMonthly ? 'py-1.5' : 'aspect-square'}
                  ${isStart || isEnd ? 'bg-brand-500 text-white shadow-sm z-10' :
                    inRange ? 'bg-brand-100 text-brand-800 rounded-none' :
                    disabled ? 'text-gray-300 cursor-not-allowed' :
                    isToday ? 'ring-2 ring-brand-300 text-brand-700 hover:bg-brand-50' :
                    'text-slate-700 hover:bg-brand-50 hover:text-brand-700'}
                  ${isStart ? 'rounded-l-xl' : ''}
                  ${isEnd ? 'rounded-r-xl' : ''}
                `}
              >
                {format(day, 'd')}
                {pricingType === 'daily' && !disabled && (() => {
                  const opt = space.pricingOptions.find((p) => p.type === 'daily');
                  if (!opt) return null;
                  const promo = (activePromos || []).find((p) =>
                    (p.spaceId === 'all' || p.spaceId === space.id) &&
                    (p.pricingType === 'all' || p.pricingType === 'daily')
                  );
                  if (!promo) return <span className="text-[10px] font-normal opacity-60">${opt.price}</span>;
                  const dp = promo.discountType === 'percent'
                    ? Math.round(opt.price * (100 - promo.discountValue) / 100)
                    : Math.max(0, opt.price - promo.discountValue);
                  return (
                    <span className={`text-[10px] font-normal ${isStart || isEnd ? 'text-white/80' : 'text-brand-600'}`}>
                      <span className="line-through opacity-50">${opt.price}</span> ${dp}
                    </span>
                  );
                })()}
              </button>
            );
          })}
        </div>
        <div className="flex gap-4 px-4 py-2.5 border-t border-gray-100 bg-gray-50 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-brand-500" /> Selected</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-xl bg-brand-100" /> Range</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-200" /> Unavailable</span>
          {selecting && <span className="ml-auto text-brand-600 font-medium">Now click an end date</span>}
        </div>
      </div>

      {/* Monthly: duration selector */}
      {isMonthly && (
        <div className="border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Duration</p>
          <div className="flex gap-2 flex-wrap">
            {MONTH_OPTIONS.map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
                  months === m ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-slate-500 hover:border-gray-300'
                }`}
              >
                {m} month{m > 1 ? 's' : ''}
              </button>
            ))}
          </div>
          {!startDate && <p className="text-xs text-slate-400 mt-2">Select a start date on the calendar above.</p>}
        </div>
      )}

      {/* Desk with named seats: available seat picker */}
      {space.seats?.length > 0 && startDate && endDate && (
        <div className="border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">
            {loadingSeats ? 'Checking availability…' : availableSeats?.length === 0 ? 'No desks available' : 'Select a desk'}
          </p>
          {loadingSeats ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
              Loading available desks…
            </div>
          ) : availableSeats?.length === 0 ? (
            <p className="text-sm text-red-500">All desks are booked for this period. Please choose different dates.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(availableSeats || []).map((seat) => (
                <button
                  key={seat}
                  onClick={() => onSeatSelect(selectedSeat === seat ? null : seat)}
                  className={`p-3 rounded-xl border text-sm font-medium text-left transition-colors ${
                    selectedSeat === seat
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-slate-600 hover:border-brand-300'
                  }`}
                >
                  {seat}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hourly: time picker */}
      {isHourly && startDate && (
        <div className="border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Time on {format(startDate, 'EEE d MMM')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Start time</label>
              <select className="input-field" value={startHour} onChange={(e) => setStartHour(+e.target.value)}>
                {hours.map((h) => <option key={h} value={h}>{format(new Date().setHours(h, 0), 'h:mm a')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">End time</label>
              <select className="input-field" value={endHour} onChange={(e) => setEndHour(+e.target.value)}>
                {hours.filter((h) => h > startHour).map((h) => <option key={h} value={h}>{format(new Date().setHours(h, 0), 'h:mm a')}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Booking summary */}
      {startDate && (endDate || isHourly) && (() => {
        const opt = space.pricingOptions.find((p) => p.type === pricingType);
        const promo = (activePromos || []).find((p) =>
          (p.spaceId === 'all' || p.spaceId === space.id) &&
          (p.pricingType === 'all' || p.pricingType === pricingType)
        );
        const baseTotal = (() => {
          if (!opt || !endDate) return opt?.price || 0;
          if (isHourly) {
            const mins = Math.round((endDate - startDate) / 60000 / 30) * 30;
            return (mins / 60) * opt.price;
          }
          if (isMonthly) return months * opt.price;
          return differenceInCalendarDays(endDate, startDate) * opt.price;
        })();
        const promoAmount = promo
          ? promo.discountType === 'percent'
            ? Math.round(baseTotal * promo.discountValue / 100)
            : Math.min(promo.discountValue, baseTotal)
          : 0;
        const finalTotal = Math.max(0, baseTotal - promoAmount);

        return (
          <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>
                <strong>{format(startDate, 'd MMM yyyy')}</strong>
                {endDate && !isHourly && ` → ${format(addDays(endDate, -1), 'd MMM yyyy')}`}
                {durationLabel() && <span className="ml-2 text-brand-600">({durationLabel()})</span>}
              </span>
              <span className="ml-auto font-bold">
                {promoAmount > 0 && <span className="line-through text-brand-400 font-normal mr-1">${baseTotal}</span>}
                ${finalTotal}
              </span>
            </div>
            {promoAmount > 0 && (
              <p className="text-xs text-brand-600 mt-1 ml-6">{promo.name} saves you ${promoAmount}</p>
            )}
          </div>
        );
      })()}

      <div className="flex justify-between pt-1">
        <div />
        <button
          onClick={() => {
            if (isHourly) {
              // If times already set (e.g. pre-filled from availability calendar), use them directly
              if (startDate && endDate) { onRangeSelect(startDate, endDate); }
              else { handleHourlyConfirm(); }
              return;
            }
            if (!startDate || !endDate) { toast.error('Please select a date range.'); return; }
            if (space.seats?.length > 0) {
              if (!selectedSeat) { toast.error('Please select a specific desk.'); return; }
              onContinue(selectedSeat);
              return;
            }
          }}
          disabled={
            !startDate || (!endDate && !isHourly) || (isHourly && !endDate && !startDate) ||
            (space.seats?.length > 0 && (!selectedSeat || availableSeats?.length === 0))
          }
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

// ─── Meeting Room Calendar (react-big-calendar, step 1 for meeting room only) ──
const meetingRoomLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { 'en-AU': enAU },
});

const MeetingRoomCalendar = ({ space, pricingType, setPricingType, onRangeSelect, activePromos }) => {
  const [view, setView] = useState('week');
  const [calDate, setCalDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotStartMin, setSlotStartMin] = useState(540); // 9:00 AM
  const [slotEndMin, setSlotEndMin] = useState(600);     // 10:00 AM
  const slotPickerRef = useRef(null);

  // 30-min slots from 7:00 AM to 7:00 PM, in minutes-from-midnight
  // 7:00 = 420, 7:30 = 450, ... 19:00 = 1140
  const slotMinutes = Array.from({ length: 25 }, (_, i) => 420 + i * 30);
  const fmtMin = (m) => format(new Date().setHours(Math.floor(m / 60), m % 60, 0, 0), 'h:mm a');
  const MIN_DURATION = 60; // 1 hour minimum

  const loadEvents = useCallback(async (rangeDate) => {
    setLoading(true);
    try {
      const start = startOfDay(addDays(rangeDate, -7));
      const end = endOfDay(addDays(rangeDate, 30));
      let bookings;
      try {
        bookings = await getBookingsForSpace('meeting-room', start, end);
      } catch {
        bookings = getMockBookings(start, end).filter((b) => b.spaceId === 'meeting-room');
      }
      setEvents(
        bookings.map((b) => ({
          id: b.id,
          title: b.memberName || 'Reserved',
          start: b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate),
          end: b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate),
        }))
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(calDate); }, [calDate, loadEvents]);

  const isDaily = pricingType === 'daily';

  const handleSelectSlot = (slotInfo) => {
    if (slotInfo.start < new Date()) return;
    // Full day: auto-set 8am–6pm and confirm immediately
    if (isDaily) {
      const start = new Date(slotInfo.start);
      start.setHours(8, 0, 0, 0);
      const end = new Date(slotInfo.start);
      end.setHours(18, 0, 0, 0);
      onRangeSelect(start, end);
      return;
    }
    // Snap clicked time to nearest 30-min slot
    const startMin = slotInfo.start.getHours() * 60 + Math.floor(slotInfo.start.getMinutes() / 30) * 30;
    const endMinRaw = slotInfo.end.getHours() * 60 + Math.ceil(slotInfo.end.getMinutes() / 30) * 30;
    // Enforce 1-hour minimum, cap end at 7:00 PM (1140)
    const endMin = Math.min(Math.max(endMinRaw, startMin + MIN_DURATION), 1140);
    setSlotStartMin(Math.max(startMin, 420));
    setSlotEndMin(endMin);
    setSelectedSlot(slotInfo);
    setTimeout(() => slotPickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const handleConfirmSlot = () => {
    if (!selectedSlot) return;
    const start = new Date(selectedSlot.start);
    start.setHours(Math.floor(slotStartMin / 60), slotStartMin % 60, 0, 0);
    const end = new Date(selectedSlot.start);
    end.setHours(Math.floor(slotEndMin / 60), slotEndMin % 60, 0, 0);
    onRangeSelect(start, end);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Pick a Time Slot</h2>
        <p className="text-sm text-slate-500 mt-1">
          Availability for <strong className="text-slate-700">{space.name}</strong>.
          {isDaily ? ' Click a day to book it (8am–6pm).' : ' Click a free slot on the calendar — minimum 1 hour, in 30-minute increments.'}
        </p>
      </div>

      {/* Pricing selector */}
      <div className="flex flex-wrap gap-2">
        {space.pricingOptions.map((opt) => {
          const promo = (activePromos || []).find((p) =>
            (p.spaceId === 'all' || p.spaceId === space.id) &&
            (p.pricingType === 'all' || p.pricingType === opt.type)
          );
          const promoPrice = promo
            ? promo.discountType === 'percent'
              ? Math.round(opt.price * (100 - promo.discountValue) / 100)
              : Math.max(0, opt.price - promo.discountValue)
            : null;
          return (
            <button
              key={opt.type}
              onClick={() => setPricingType(opt.type)}
              className={`text-sm px-4 py-2 rounded-xl border font-medium transition-colors ${
                pricingType === opt.type
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-200 text-slate-500 hover:border-gray-300'
              }`}
            >
              {opt.label} —{' '}
              {promoPrice !== null ? (
                <>
                  <span className="line-through text-slate-400 font-normal">${opt.price}</span>{' '}
                  <strong className="text-brand-600">${promoPrice}</strong>
                </>
              ) : (
                <strong>${opt.price}</strong>
              )}
            </button>
          );
        })}
      </div>

      {/* Hourly: week/day calendar */}
      {!isDaily && (
        <div className="rounded-2xl overflow-hidden border border-gray-200">
          {loading && (
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-slate-400">
              <span className="w-3.5 h-3.5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
              Loading availability…
            </div>
          )}
          <Calendar
            localizer={meetingRoomLocalizer}
            events={(() => {
              if (!selectedSlot) return events;
              const previewStart = new Date(selectedSlot.start);
              previewStart.setHours(Math.floor(slotStartMin / 60), slotStartMin % 60, 0, 0);
              const previewEnd = new Date(selectedSlot.start);
              previewEnd.setHours(Math.floor(slotEndMin / 60), slotEndMin % 60, 0, 0);
              return [...events, { __preview: true, title: 'Your selection', start: previewStart, end: previewEnd }];
            })()}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={calDate}
            onNavigate={setCalDate}
            min={new Date(0, 0, 0, 7, 0)}
            max={new Date(0, 0, 0, 20, 0)}
            step={30}
            timeslots={2}
            views={['week', 'day']}
            culture="en-AU"
            style={{ height: 500 }}
            selectable
            onSelectSlot={handleSelectSlot}
            eventPropGetter={(event) => ({
              style: event?.__preview ? {
                backgroundColor: '#10b981',
                borderColor: '#059669',
                color: '#fff',
                borderRadius: '4px',
                fontSize: '12px',
                padding: '1px 4px',
                fontWeight: 600,
              } : {
                backgroundColor: space.color,
                borderColor: space.color,
                color: '#fff',
                borderRadius: '4px',
                fontSize: '12px',
                padding: '1px 4px',
              },
            })}
            messages={{
              today: 'Today',
              previous: '←',
              next: '→',
              week: 'Week',
              day: 'Day',
              noEventsInRange: 'No bookings — fully available!',
            }}
          />
        </div>
      )}

      {/* Full day: available days as buttons */}
      {isDaily && (() => {
        const today = startOfDay(new Date());
        const days = eachDayOfInterval({ start: today, end: addDays(today, 27) }); // next 4 weeks
        // A day is unavailable for full-day booking if ANY booking overlaps with it
        const isDayBooked = (day) => events.some((e) => {
          const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
          return e.start < dayEnd && e.end > dayStart;
        });

        return (
          <div>
            {loading && (
              <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
                <span className="w-3.5 h-3.5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                Loading availability…
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {days.map((day) => {
                const booked = isDayBooked(day);
                const isWeekend = getDay(day) === 0 || getDay(day) === 6;
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => {
                      const s = new Date(day); s.setHours(8, 0, 0, 0);
                      const e = new Date(day); e.setHours(18, 0, 0, 0);
                      onRangeSelect(s, e);
                    }}
                    disabled={booked}
                    className={`p-3 rounded-xl border text-center transition-colors ${
                      booked
                        ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                        : isWeekend
                          ? 'border-gray-200 text-slate-400 hover:border-brand-300 hover:bg-brand-50'
                          : 'border-gray-200 text-slate-700 hover:border-brand-500 hover:bg-brand-50'
                    }`}
                  >
                    <div className="text-xs text-slate-400 font-medium">{format(day, 'EEE')}</div>
                    <div className="text-lg font-bold">{format(day, 'd')}</div>
                    <div className="text-xs text-slate-400">{format(day, 'MMM')}</div>
                    {booked && <div className="text-[10px] text-red-400 mt-0.5">Booked</div>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Hourly: slot time picker */}
      {selectedSlot && !isDaily && (
        <div ref={slotPickerRef} className="border border-brand-200 rounded-xl p-4 bg-brand-50/50 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">
              {format(selectedSlot.start, 'EEEE, d MMMM yyyy')}
            </p>
            <button onClick={() => setSelectedSlot(null)} className="text-slate-400 hover:text-slate-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Start time</label>
              <select
                className="input-field"
                value={slotStartMin}
                onChange={(e) => {
                  const m = +e.target.value;
                  setSlotStartMin(m);
                  // Bump end time if it's now less than 1hr after start
                  if (slotEndMin < m + MIN_DURATION) setSlotEndMin(m + MIN_DURATION);
                }}
              >
                {slotMinutes.filter((m) => m <= 1140 - MIN_DURATION).map((m) => (
                  <option key={m} value={m}>{fmtMin(m)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">End time</label>
              <select
                className="input-field"
                value={slotEndMin}
                onChange={(e) => setSlotEndMin(+e.target.value)}
              >
                {slotMinutes.filter((m) => m >= slotStartMin + MIN_DURATION).map((m) => (
                  <option key={m} value={m}>{fmtMin(m)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs">
              <div className="text-slate-600">
                Duration: <strong>{(() => {
                  const hrs = (slotEndMin - slotStartMin) / 60;
                  return hrs === Math.floor(hrs) ? `${hrs} hr${hrs !== 1 ? 's' : ''}` : `${hrs} hrs`;
                })()}</strong>
              </div>
              <div className="text-slate-400 mt-0.5">Minimum 1 hour · 30-min increments</div>
            </div>
            <button onClick={handleConfirmSlot} className="btn-primary">
              Book This Slot →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Step 2: Your Details ─────────────────────────────────────────────────────
const DetailsStep = ({ onNext, onBack, existingUser, defaultValues }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues });

  // Re-sync form if defaultValues populate after mount (e.g. userProfile loads async)
  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800 mb-2">Your Details</h2>
      {existingUser ? (
        <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800">
          Booking as <strong>{existingUser.displayName || existingUser.email}</strong>.
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          We'll create a member account for you. Already have one?{' '}
          <Link to="/login" className="underline font-medium">Sign in first</Link>.
        </div>
      )}

      {/* Name — hidden for logged-in users (pre-filled from profile) */}
      {existingUser ? (
        <input type="hidden" {...register('firstName')} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">First Name *</label>
            <input className="input-field" {...register('firstName', { required: 'Required' })} />
            {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="label">Last Name *</label>
            <input className="input-field" {...register('lastName', { required: 'Required' })} />
            {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
          </div>
        </div>
      )}
      {existingUser && <input type="hidden" {...register('lastName')} />}

      {/* Email — read-only for logged-in users */}
      {existingUser ? (
        <div>
          <label className="label">Email Address</label>
          <input type="email" className="input-field bg-gray-50 text-slate-500 cursor-not-allowed" readOnly {...register('email')} />
        </div>
      ) : (
        <div>
          <label className="label">Email Address *</label>
          <input type="email" className="input-field" {...register('email', { required: 'Required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })} />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>
      )}

      {!existingUser && (
        <div>
          <label className="label">Password * <span className="text-gray-400 font-normal">(min. 8 characters)</span></label>
          <input type="password" className="input-field" {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })} />
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Phone <span className="text-slate-400 font-normal text-xs">(optional)</span></label>
          <input type="tel" className="input-field" {...register('phone')} placeholder="04XX XXX XXX or +61 4XX XXX XXX" />
          <p className="text-xs text-slate-400 mt-1">Add your number to receive SMS updates about your booking.</p>
        </div>
        <div>
          <label className="label">Business / Company</label>
          <input className="input-field" {...register('company')} placeholder="Optional" />
        </div>
      </div>
      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary">Back</button>
        <button type="submit" className="btn-primary">Continue →</button>
      </div>
    </form>
  );
};

// ─── Stripe payment form (inner component needing Elements context) ────────────
const StripeForm = ({ onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/booking-complete` },
      redirect: 'if_required',
    });
    if (error) {
      onError(error.message);
    } else {
      onSuccess(paymentIntent?.id);
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ wallets: { applePay: 'never', googlePay: 'never' }, layout: 'tabs' }} />
      <button type="submit" disabled={!stripe || processing} className="btn-primary w-full mt-2">
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing…
          </span>
        ) : 'Pay Now'}
      </button>
    </form>
  );
};

// ─── Step 3: Payment & Terms ──────────────────────────────────────────────────
const PaymentStep = ({ price, originalPrice, creditsApplied, creditDiscount, promoName, promoDiscount, pricingType, space, bookingId, userData, startDate, endDate, onSuccess, onBack }) => {
  const isMonthly = pricingType === 'monthly';
  const isDedicatedOffice = space?.id === 'dedicated-desk';
  const bankOnly = isDedicatedOffice;
  const [paymentMethod, setPaymentMethod] = useState((isMonthly || bankOnly) ? 'bank' : 'card');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const sigRef = useRef(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [bankConfirming, setBankConfirming] = useState(false);

  useEffect(() => {
    if (paymentMethod === 'card' && stripePromise && price > 0) {
      setLoadingSecret(true);
      createPaymentIntent({ amount: price * 100, bookingId: bookingId || 'pending', currency: 'aud' })
        .then(({ clientSecret }) => setClientSecret(clientSecret))
        .catch(() => setClientSecret(null))
        .finally(() => setLoadingSecret(false));
    }
  }, [paymentMethod, price]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFree = price === 0;
  const [freeConfirming, setFreeConfirming] = useState(false);

  const canProceed = termsAccepted && !!signatureData;

  const handleBankConfirm = () => {
    if (!termsAccepted) { toast.error('Please accept the terms and conditions.'); return; }
    if (!signatureData) { toast.error('Please sign the terms to proceed.'); return; }
    setBankConfirming(true);
    setTimeout(() => { onSuccess('bank_deposit', signatureData); setBankConfirming(false); }, 800);
  };

  const handleFreeConfirm = () => {
    if (!termsAccepted) { toast.error('Please accept the terms and conditions.'); return; }
    if (!signatureData) { toast.error('Please sign the terms to proceed.'); return; }
    setFreeConfirming(true);
    setTimeout(() => { onSuccess('credits', signatureData); setFreeConfirming(false); }, 600);
  };

  const handleClearSignature = () => {
    sigRef.current?.clear();
    setSignatureData(null);
  };

  const handleSignatureEnd = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      // Composite onto a white background so the JPEG export doesn't fill transparent
      // pixels with black. JPEG keeps the document small (well under Firestore's 1MB limit).
      const src = sigRef.current.getCanvas();
      const out = document.createElement('canvas');
      out.width = src.width;
      out.height = src.height;
      const ctx = out.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(src, 0, 0);
      setSignatureData(out.toDataURL('image/jpeg', 0.5));
    }
  };

  const billingNote = pricingType === 'monthly'
    ? 'Your card will be charged monthly in advance on your billing date.'
    : 'Your card will be charged now.';

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-slate-800">Terms & {isFree ? 'Confirmation' : 'Payment'}</h2>

      {/* Price summary */}
      <div className="card p-4 bg-slate-50">
        <div className="flex justify-between items-center text-sm mb-1">
          <span className="text-slate-500">{space?.name}</span>
          <div className="text-right">
            {originalPrice > 0 && (
              <span className="text-xs text-slate-400 line-through mr-2">${originalPrice}</span>
            )}
            <span className="font-semibold text-slate-700">${price} AUD</span>
          </div>
        </div>
        {promoDiscount > 0 && (
          <div className="flex justify-between items-center text-xs text-brand-600 mt-0.5">
            <span>{promoName || 'Promotion'}</span>
            <span>-${promoDiscount}</span>
          </div>
        )}
        {creditsApplied > 0 && (
          <div className="flex justify-between items-center text-xs text-emerald-700 mt-0.5">
            <span>Meeting room credits ({creditsApplied} hr{creditsApplied !== 1 ? 's' : ''})</span>
            <span>-${creditDiscount}</span>
          </div>
        )}
        {startDate && (
          <div className="text-xs text-slate-400 mt-1">
            {format(startDate, 'd MMM yyyy')}
            {endDate && pricingType !== 'hourly' && ` → ${format(addDays(endDate, -1), 'd MMM yyyy')}`}
            {endDate && pricingType === 'hourly' && `, ${format(startDate, 'h:mm a')} – ${format(endDate, 'h:mm a')}`}
          </div>
        )}
      </div>

      {/* ── Step 1: Terms & Conditions ─────────────────────────────────────────── */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Step 1 — Review & Sign Terms</p>

        {/* Terms accordion */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setTermsExpanded(!termsExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-gray-100 transition-colors text-sm font-medium text-slate-700"
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Membership Terms & Conditions
            </span>
            <svg className={`w-4 h-4 text-slate-400 transition-transform ${termsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {termsExpanded && (
            <div className="p-4 text-sm text-slate-600 space-y-4 max-h-96 overflow-y-auto leading-relaxed border-t border-gray-200">
              <p className="font-bold text-slate-800 text-base">The Hub Crookwell — Membership Terms & Conditions</p>
              {TERMS_SECTIONS.map((section) => (
                <div key={section.heading}>
                  <p className="font-semibold text-slate-700 mb-1">{section.heading}</p>
                  <div className="space-y-1.5">
                    {section.clauses.map((c) => (
                      <p key={c.id} className="text-slate-600">
                        <span className="font-medium text-slate-700">{c.id} {c.title}</span>{' '}{c.text}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <p className="font-semibold text-slate-700 mb-1">Annex A — House Rules</p>
                <div className="space-y-1.5">
                  {HOUSE_RULES.map((r) => (
                    <p key={r.id} className="text-slate-600">
                      <span className="font-medium text-slate-700">{r.id}. {r.title}</span>{' '}{r.text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Accept checkbox */}
        <div className="flex items-start gap-3 mt-3">
          <input
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-500 cursor-pointer"
          />
          <label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
            I have read and accept the <button onClick={() => setTermsExpanded(true)} className="text-brand-500 underline">Membership Terms & Conditions</button>.
          </label>
        </div>

        {/* Signature Pad */}
        {termsAccepted && (
          <div className="mt-4 space-y-2">
            <label className="text-sm font-medium text-slate-700">Sign below to confirm your agreement</label>
            <div className="border-2 border-dashed border-slate-300 rounded-xl bg-white overflow-hidden" style={{ touchAction: 'none' }}>
              <SignatureCanvas
                ref={sigRef}
                penColor="#1e293b"
                canvasProps={{ className: 'w-full', style: { width: '100%', height: 160 } }}
                onEnd={handleSignatureEnd}
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                {signatureData ? (
                  <span className="text-green-600 font-medium">Signature captured</span>
                ) : (
                  'Use your mouse or finger to sign'
                )}
              </p>
              <button type="button" onClick={handleClearSignature} className="text-xs text-slate-400 hover:text-slate-600 underline">
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Step 2: Payment ────────────────────────────────────────────────────── */}
      {isFree ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          This booking is fully covered by your meeting room credits. No payment required.
        </div>
      ) : (
        <div className={!canProceed ? 'opacity-50 pointer-events-none' : ''}>
          <p className="text-sm font-semibold text-slate-700 mb-2">Step 2 — Payment</p>

          {/* Payment method */}
          <div>
            <div className="grid grid-cols-2 gap-3">
              {[
                !bankOnly && { id: 'card', label: 'Credit / Debit Card', icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                )},
                (isMonthly || bankOnly) && { id: 'bank', label: 'Bank Deposit', icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>
                )},
              ].filter(Boolean).map((m) => (
                <button
                  key={m.id}
                  onClick={() => setPaymentMethod(m.id)}
                  className={`flex items-center gap-2.5 p-3.5 rounded-xl border-2 text-sm font-medium transition-colors ${
                    paymentMethod === m.id
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-slate-600 hover:border-gray-300'
                  }`}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Credit card: Stripe */}
          {paymentMethod === 'card' && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 mb-3">{billingNote}</p>
              {!stripePromise ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  Stripe is not configured yet. Add <code className="bg-amber-100 px-1 rounded">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your .env file.
                </div>
              ) : loadingSecret ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
                  <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                  Initialising payment…
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                  {canProceed ? (
                    <StripeForm
                      onSuccess={(intentId) => onSuccess('card', intentId, signatureData)}
                      onError={(msg) => toast.error(msg)}
                    />
                  ) : (
                    <button disabled className="btn-primary w-full opacity-50 cursor-not-allowed">Complete terms above to pay</button>
                  )}
                </Elements>
              ) : (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-700">
                  Could not connect to payment service. Please try bank deposit or contact us.
                </div>
              )}
            </div>
          )}

          {/* Bank deposit */}
          {paymentMethod === 'bank' && (
            <div className="mt-3 border border-gray-200 rounded-xl p-4 space-y-3 text-sm">
              <p className="font-semibold text-slate-700">Bank Transfer Details</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-slate-600">
                <div className="flex justify-between"><span className="text-slate-400">Account Name</span><span className="font-medium">The Hub Crookwell</span></div>
                <div className="flex justify-between"><span className="text-slate-400">BSB</span><span className="font-medium">— TBC —</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Account No.</span><span className="font-medium">— TBC —</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Reference</span><span className="font-medium">{userData?.lastName?.toUpperCase() || 'YOUR NAME'}</span></div>
              </div>
              <p className="text-xs text-slate-400">
                An invoice will be emailed to <strong>{userData?.email}</strong>. Your booking is held for 48 hours pending payment. Please use your last name as the payment reference.
              </p>
              <button
                onClick={handleBankConfirm}
                disabled={bankConfirming || !canProceed}
                className="btn-primary w-full disabled:opacity-50"
              >
                {bankConfirming ? 'Confirming…' : 'Confirm Bank Deposit Booking'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-1">
        <button onClick={onBack} className="btn-secondary">Back</button>
        {isFree && (
          <button
            onClick={handleFreeConfirm}
            disabled={freeConfirming || !canProceed}
            className="btn-primary disabled:opacity-50"
          >
            {freeConfirming ? 'Confirming…' : 'Confirm Booking →'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Step 4: Confirmation ─────────────────────────────────────────────────────
const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v.toDate === 'function') return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const ConfirmationStep = ({ booking, space, userData, paymentMethod }) => (
  <div className="text-center space-y-6">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <div>
      <h2 className="text-2xl font-extrabold text-slate-800">
        {paymentMethod === 'bank_deposit' ? 'Booking Reserved!' : 'Booking Confirmed!'}
      </h2>
      <p className="text-slate-500 mt-2">
        {paymentMethod === 'bank_deposit'
          ? 'Your booking is held for 48 hours. An invoice has been sent to your email — please complete your bank transfer to confirm.'
          : 'Payment received. Your booking is confirmed. Check your email for access details.'}
      </p>
    </div>

    <div className="card p-6 text-left max-w-md mx-auto">
      <h3 className="font-semibold text-slate-700 mb-4 text-sm">Booking Summary</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-400">Space</span><span className="font-medium">{space?.name}</span></div>
        {toDate(booking?.startDate) && (
          <div className="flex justify-between"><span className="text-slate-400">Start</span><span className="font-medium">{format(toDate(booking.startDate), 'd MMM yyyy')}</span></div>
        )}
        {toDate(booking?.endDate) && (
          <div className="flex justify-between"><span className="text-slate-400">End</span><span className="font-medium">{format(addDays(toDate(booking.endDate), -1), 'd MMM yyyy')}</span></div>
        )}
        <div className="flex justify-between">
          <span className="text-slate-400">Payment</span>
          <span className="font-medium">{paymentMethod === 'bank_deposit' ? 'Bank Deposit' : 'Card — Paid'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Status</span>
          <span className={`font-semibold ${paymentMethod === 'bank_deposit' ? 'text-amber-600' : 'text-green-600'}`}>
            {paymentMethod === 'bank_deposit' ? 'Pending Payment' : 'Confirmed'}
          </span>
        </div>
      </div>
    </div>

    <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800 text-left">
      <p className="font-semibold mb-1">What happens next?</p>
      <ul className="space-y-1 text-brand-700 list-disc list-inside">
        <li>A confirmation email has been sent to <strong>{userData?.email}</strong></li>
        {paymentMethod === 'bank_deposit'
          ? <li>Complete your bank transfer within 48 hours to secure your booking</li>
          : <li>Access codes and Wi-Fi details will arrive within 1 hour</li>}
        <li>Contact <a href="mailto:hello@thehubcrookwell.com.au" className="underline">hello@thehubcrookwell.com.au</a> for any questions</li>
      </ul>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link to="/dashboard" className="btn-primary">View My Dashboard</Link>
      <Link to="/" className="btn-secondary">Back to Home</Link>
    </div>
  </div>
);


// ─── Main BookingPage ─────────────────────────────────────────────────────────
export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile, register: registerUser } = useAuth();
  const spaces = useSpaces();

  const initialSpaceId = searchParams.get('space') || spaces[0].id;
  const initialPricing = searchParams.get('pricing') || spaces.find((s) => s.id === initialSpaceId)?.pricingOptions[0].type;
  const prestart = searchParams.get('prestart');
  const preend = searchParams.get('preend');

  const [step, setStep] = useState(searchParams.get('space') ? 1 : 0);
  const [selectedSpaceId, setSelectedSpaceId] = useState(initialSpaceId);
  const [pricingType, setPricingType] = useState(initialPricing);
  const [startDate, setStartDate] = useState(prestart ? new Date(+prestart) : null);
  const [endDate, setEndDate] = useState(preend ? new Date(+preend) : null);
  const [userData, setUserData] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [booking, setBooking] = useState(null);
  const [pendingBookingData, setPendingBookingData] = useState(null); // stored until payment completes
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [bankDetails, setBankDetails] = useState({});
  const [bookingsEnabled, setBookingsEnabled] = useState(null); // null = loading

  useEffect(() => {
    getDoc(doc(db, 'config', 'bankDetails'))
      .then((snap) => { if (snap.exists()) setBankDetails(snap.data()); })
      .catch(() => {});
    getDoc(doc(db, 'config', 'features'))
      .then((snap) => { setBookingsEnabled(snap.exists() ? (snap.data().bookingsEnabled ?? false) : false); })
      .catch(() => setBookingsEnabled(false));
  }, []);

  // ── Promotions ──────────────────────────────────────────────────────────────
  const activePromos = useActivePromos();

  const space = spaces.find((s) => s.id === selectedSpaceId);
  const basePrice = calcPrice(space, pricingType, startDate, endDate);

  // Find matching promo: purchase window must include today, booking start must be in applicable dates
  const matchingPromo = startDate ? findPromo(activePromos, selectedSpaceId, pricingType, startDate) : null;

  const promoDiscount = matchingPromo
    ? matchingPromo.discountType === 'percent'
      ? Math.round(basePrice * Math.min(matchingPromo.discountValue, 100) / 100)
      : Math.min(matchingPromo.discountValue, basePrice)
    : 0;
  const price = Math.max(0, basePrice - promoDiscount);

  // ── Meeting room credits ──────────────────────────────────────────────────
  const [creditBalance, setCreditBalance] = useState(null); // { month, total, used, available }
  const [pendingCredits, setPendingCredits] = useState(null); // { applied, month }

  useEffect(() => {
    if (!currentUser || space?.type !== 'room') { setCreditBalance(null); return; }
    getMemberCreditBalance(currentUser.uid).then(setCreditBalance).catch(() => setCreditBalance(null));
  }, [currentUser?.uid, space?.type]); // eslint-disable-line

  const hourlyRate = space?.pricingOptions?.find((p) => p.type === 'hourly')?.price || 35;
  const equivalentHours = (() => {
    if (!startDate || !endDate || space?.type !== 'room') return 0;
    if (pricingType === 'hourly') {
      // Round to nearest 30 min, then convert to fractional hours
      const mins = Math.round((endDate - startDate) / 60000 / 30) * 30;
      return mins / 60;
    }
    if (pricingType === 'half-day') return 4;
    if (pricingType === 'daily') return 8;
    return 0;
  })();
  const creditsToApply = (space?.type === 'room' && currentUser && (creditBalance?.available ?? 0) > 0)
    ? Math.min(creditBalance.available, equivalentHours) : 0;
  const creditDiscount = creditsToApply * hourlyRate;
  const adjustedPrice = Math.max(0, price - creditDiscount);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentUser && userProfile) {
      setUserData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: currentUser.email || '',
        phone: userProfile.phone || '',
        company: userProfile.company || '',
      });
    }
  }, [currentUser, userProfile]);

  const handleDetailsNext = async (data, overrideStart, overrideEnd, seatOverride) => {
    const start = overrideStart instanceof Date ? overrideStart : startDate;
    const end = overrideEnd instanceof Date ? overrideEnd : endDate;
    if (!(start instanceof Date) || isNaN(start)) {
      console.error('[handleDetailsNext] Invalid startDate:', start, 'overrideStart:', overrideStart, 'startDate state:', startDate);
      toast.error('Please select your booking dates first.');
      setStep(1);
      return;
    }
    try {
      let uid = currentUser?.uid;
      if (!currentUser) {
        const user = await registerUser(data);
        uid = user.uid;
        await sendWelcomeEmail({ to: data.email, name: data.firstName }).catch(() => {});
      }
      setUserData(data);
      const rawPrice = calcPrice(space, pricingType, start, end);
      const unitPrice = space?.pricingOptions.find((p) => p.type === pricingType)?.price;
      const seatName = seatOverride !== undefined ? seatOverride : selectedSeat;

      // Apply promotion
      const currentPromoDiscount = matchingPromo
        ? matchingPromo.discountType === 'percent'
          ? Math.round(rawPrice * matchingPromo.discountValue / 100)
          : Math.min(matchingPromo.discountValue, rawPrice)
        : 0;
      const currentPrice = Math.max(0, rawPrice - currentPromoDiscount);

      // Compute credits to apply for this booking
      const hr = space?.pricingOptions?.find((p) => p.type === 'hourly')?.price || 35;
      const eqHrs = (space?.type === 'room' && start && end) ? (
        pricingType === 'hourly' ? Math.round((end - start) / 60000 / 30) * 30 / 60 :
        pricingType === 'half-day' ? 4 :
        pricingType === 'daily' ? 8 : 0
      ) : 0;
      const applied = currentUser && creditBalance?.available > 0 ? Math.min(creditBalance.available, eqHrs) : 0;
      const discount = applied * hr;
      const finalPrice = Math.max(0, currentPrice - discount);

      // Store booking data — don't write to Firestore yet (only on payment success)
      const bookingData = {
        userId: uid,
        spaceId: selectedSpaceId,
        pricingType,
        startDate: start,
        endDate: end,
        price: finalPrice,
        unitPrice: unitPrice || null,
        ...(currentPromoDiscount > 0 ? { promoId: matchingPromo.id, promoName: matchingPromo.name, promoDiscount: currentPromoDiscount, originalPrice: rawPrice } : {}),
        ...(applied > 0 ? { originalPrice: rawPrice, creditsApplied: applied, creditMonth: creditBalance.month } : {}),
        ...(seatName ? { seatName } : {}),
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: normalisePhone(data.phone),
        company: data.company || '',
      };
      if (applied > 0) setPendingCredits({ applied, month: creditBalance.month });
      setPendingBookingData(bookingData);
      setBooking({ startDate: start, endDate: end, spaceId: selectedSpaceId });
      setStep(3);
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handlePaymentSuccess = async (method, paymentIntentIdOrSig, signatureDataUrl) => {
    // For card: (method, intentId, signatureDataUrl)
    // For bank/credits: (method, signatureDataUrl)
    const isCard = method === 'card';
    const paymentIntentId = isCard ? paymentIntentIdOrSig : undefined;
    const sigData = isCard ? signatureDataUrl : paymentIntentIdOrSig;

    setPaymentMethod(method);
    setStep(4);

    // NOW create the booking in Firestore — only after payment/terms are complete
    let bid = null;
    if (pendingBookingData) {
      try {
        // Generate access PIN at creation time if feature is enabled
        let accessFields = {};
        if (currentUser) {
          try {
            const accessEnabled = await getAccessFeatureEnabled();
            if (accessEnabled) {
              const pin = await getOrCreateAccessPin(currentUser.uid);
              accessFields = { accessPin: pin, accessCodeStatus: 'pending' };
            }
          } catch { /* access feature check failed — continue without PIN */ }
        }
        bid = await createBooking({
          ...pendingBookingData,
          paymentMethod: method,
          paymentStatus: isCard ? 'paid' : 'pending',
          status: 'confirmed',
          termsAccepted: true,
          termsAcceptedAt: new Date(),
          ...(sigData ? { signature: sigData, signedAt: new Date() } : {}),
          ...(paymentIntentId ? { stripePaymentIntentId: paymentIntentId } : {}),
          ...accessFields,
        });
        setBookingId(bid);
      } catch (err) {
        console.error('[Firestore] createBooking failed:', err);
        console.error('[Firestore] Error code:', err?.code);
        console.error('[Firestore] Error message:', err?.message);
        toast.error(`Booking save failed: ${err?.message || 'Unknown error'}. Please contact admin.`);
        bid = `mock-${Date.now()}`;
        setBookingId(bid);
      }
    }
    // Deduct meeting room credits if applied
    if (pendingCredits?.applied > 0 && currentUser) {
      deductMeetingRoomCredits(currentUser.uid, pendingCredits.applied, pendingCredits.month).catch(() => {});
      setPendingCredits(null);
      getMemberCreditBalance(currentUser.uid).then(setCreditBalance).catch(() => {});
    }
    // Provision meeting room credits for new monthly memberships
    if (pricingType === 'monthly' && currentUser) {
      const name = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();
      provisionMonthlyCredits(currentUser.uid, userData?.email || '', name, bid).catch(() => {});
    }
    // Provision meeting room credits for day passes (if configured on pricing option)
    if (pricingType === 'daily' && currentUser) {
      const opt = space?.pricingOptions?.find((p) => p.type === 'daily');
      const dayCredits = opt?.meetingRoomCredits;
      if (dayCredits > 0) {
        const name = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();
        provisionDailyPassCredits(currentUser.uid, userData?.email || '', name, dayCredits, bid).catch(() => {});
      }
    }
    // Send confirmation email
    if (userData?.email) {
      const isHourlyBooking = pricingType === 'hourly';
      const fmtStart = startDate
        ? (isHourlyBooking ? format(startDate, 'd MMM yyyy, h:mm a') : format(startDate, 'd MMM yyyy'))
        : '';
      const fmtEnd = endDate
        ? (isHourlyBooking ? format(endDate, 'd MMM yyyy, h:mm a') : format(addDays(endDate, -1), 'd MMM yyyy'))
        : '';
      sendBookingConfirmation({
        to: userData.email,
        name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email,
        bookingId: bid || '',
        spaceName: space?.name || '',
        seatName: selectedSeat || '',
        startDate: fmtStart,
        endDate: fmtEnd,
        price: adjustedPrice,
        pricingType,
        paymentMethod: method,
        bsb: bankDetails.bsb || '',
        accountNumber: bankDetails.accountNumber || '',
        accountName: bankDetails.accountName || '',
      }).catch(() => {});
    }
    if (userData?.phone) {
      const isHourlyBooking = pricingType === 'hourly';
      sendBookingSms({
        to: userData.phone,
        name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email,
        spaceName: space?.name || '',
        startDate: startDate ? (isHourlyBooking ? format(startDate, 'd MMM yyyy, h:mm a') : format(startDate, 'd MMM yyyy')) : '',
        endDate: endDate ? (isHourlyBooking ? format(endDate, 'd MMM yyyy, h:mm a') : format(addDays(endDate, -1), 'd MMM yyyy')) : '',
        bookingId: bid || '',
      }).catch(() => {});
    }
    toast.success(method === 'bank_deposit' ? 'Booking reserved! Check your email for payment details.' : 'Payment successful! Confirmation email sent.');
    // Generate tax invoice for card payments
    if (method === 'card' && bid && !bid.startsWith('mock-')) {
      generateInvoice(bid).catch(() => {});
    }
  };

  if (bookingsEnabled === false) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="card p-10 space-y-5">
            <div className="w-16 h-16 bg-brand-50 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-extrabold text-slate-800">Coming Soon</h1>
            <p className="text-slate-500">
              Online bookings are opening soon. In the meantime, get in touch to arrange your workspace.
            </p>
            <div className="pt-2 border-t border-gray-100 text-left space-y-2">
              <h3 className="text-sm font-semibold text-slate-700 text-center">Contact & Hours</h3>
              <div className="space-y-1.5 text-sm text-slate-500">
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  63 Goulburn Street, Crookwell NSW 2583, Australia
                </p>
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:admin@thehubcrookwell.com.au" className="text-brand-500 hover:underline">admin@thehubcrookwell.com.au</a>
                </p>
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Mon–Fri 8am–6pm · Sat 9am–1pm
                </p>
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Members: Mon–Saturday
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-slate-800">Book a Space</h1>
          <p className="text-slate-500 mt-1">Complete the steps below to secure your workspace.</p>
        </div>

        <StepBar current={step} />

        <div className="card p-6 md:p-8">
          {/* Step 0: Choose space */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-800">Choose a Space</h2>
              <p className="text-sm text-slate-500">Select the type of workspace you need.</p>
              <div className="grid grid-cols-1 gap-4 mt-2">
                {spaces.map((s) => (
                  <SpaceCard key={s.id} space={s} selected={selectedSpaceId === s.id}
                    onSelect={(id) => { setSelectedSpaceId(id); setPricingType(spaces.find((sp) => sp.id === id)?.pricingOptions[0].type); setStartDate(null); setEndDate(null); }}
                  />
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={() => setStep(1)} className="btn-primary">Check Availability →</button>
              </div>
            </div>
          )}

          {/* Step 1: Availability + date range */}
          {step === 1 && space && (
            <div>
              <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-5 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Change space
              </button>
              {/* Credit balance banner (meeting room + logged-in members only) */}
              {space.type === 'room' && currentUser && creditBalance !== null && (
                <div className={`mb-4 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
                  creditBalance.available > 0
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-gray-200 bg-gray-50 text-slate-500'
                }`}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {creditBalance.available > 0 ? (
                    <span>
                      <strong>{creditBalance.available} meeting room credit hr{creditBalance.available !== 1 ? 's' : ''}</strong> available this month.
                      {equivalentHours > 0 && creditsToApply > 0 && (
                        <span className="ml-1">{creditsToApply} hr{creditsToApply !== 1 ? 's' : ''} will be applied → saves <strong>${creditDiscount}</strong>.</span>
                      )}
                    </span>
                  ) : (
                    <span>No meeting room credits remaining this month ({creditBalance.used}/{creditBalance.total} hrs used).</span>
                  )}
                </div>
              )}
              {space.type === 'room' ? (
                <MeetingRoomCalendar
                  space={space}
                  pricingType={pricingType}
                  setPricingType={setPricingType}
                  activePromos={activePromos}
                  onRangeSelect={(s, e) => {
                    setStartDate(s);
                    setEndDate(e);
                    setTimeout(() => {
                      if (currentUser && userData) {
                        handleDetailsNext(userData, s, e);
                      } else {
                        setStep(2);
                      }
                    }, 200);
                  }}
                />
              ) : (
                <AvailabilityCalendar
                  space={space}
                  pricingType={pricingType}
                  setPricingType={(t) => { setPricingType(t); setStartDate(null); setEndDate(null); setSelectedSeat(null); }}
                  activePromos={activePromos}
                  startDate={startDate}
                  endDate={endDate}
                  selectedSeat={selectedSeat}
                  onSeatSelect={setSelectedSeat}
                  onRangeSelect={(s, e) => {
                    setStartDate(s);
                    setEndDate(e);
                    setSelectedSeat(null);
                    const isDeskWithSeats = space?.seats?.length > 0;
                    if (s && e && !isDeskWithSeats) {
                      setTimeout(() => {
                        if (currentUser && userData) {
                          handleDetailsNext(userData, s, e);
                        } else {
                          setStep(2);
                        }
                      }, 200);
                    }
                  }}
                  onContinue={(seat) => {
                    setSelectedSeat(seat);
                    if (currentUser && userData) {
                      handleDetailsNext(userData, startDate, endDate, seat);
                    } else {
                      setStep(2);
                    }
                  }}
                />
              )}
            </div>
          )}

          {/* Step 2: Your details */}
          {step === 2 && (
            <DetailsStep
              onNext={handleDetailsNext}
              onBack={() => setStep(1)}
              existingUser={currentUser}
              defaultValues={userData || { firstName: '', lastName: '', email: '', phone: '', company: '' }}
            />
          )}

          {/* Step 3: Payment & Terms */}
          {step === 3 && (
            <PaymentStep
              price={adjustedPrice}
              originalPrice={(creditsToApply > 0 || promoDiscount > 0) ? basePrice : null}
              creditsApplied={creditsToApply}
              creditDiscount={creditDiscount}
              promoName={matchingPromo?.name}
              promoDiscount={promoDiscount}
              pricingType={pricingType}
              space={space}
              bookingId={bookingId}
              userData={userData}
              startDate={startDate}
              endDate={endDate}
              onSuccess={handlePaymentSuccess}
              onBack={() => setStep(currentUser && userData ? 1 : 2)}
            />
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <ConfirmationStep booking={booking} space={space} userData={userData} paymentMethod={paymentMethod} />
          )}
        </div>

        {/* Booking bar */}
        {step >= 1 && step <= 3 && (
          <div className="mt-4 card p-4 flex flex-wrap gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: space?.color }} />
              <span className="font-medium text-slate-700">{space?.name}</span>
            </div>
            {startDate && endDate && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {pricingType === 'hourly'
                  ? `${format(startDate, 'EEE d MMM, h:mm')}–${format(endDate, 'h:mm a')}`
                  : `${format(startDate, 'd MMM')} → ${format(addDays(endDate, -1), 'd MMM yyyy')}`}
              </div>
            )}
            {adjustedPrice > 0 && (
              <div className="ml-auto font-bold text-slate-800 flex items-center gap-1">
                {creditsToApply > 0 && price > 0 && (
                  <span className="text-xs font-normal text-slate-400 line-through">${price}</span>
                )}
                ${adjustedPrice} <span className="font-normal text-slate-400 text-xs">AUD</span>
              </div>
            )}
            {adjustedPrice === 0 && price > 0 && (
              <div className="ml-auto font-bold text-emerald-600 text-sm">
                Free (credits applied)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
